import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { PeriodMetric } from "../../models/models";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { getUiLocale } from "../../../../shared/utils/relativeTime";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface CostTrendChartProps {
  costTrend: PeriodMetric[];
}

const CostTrendChart: React.FC<CostTrendChartProps> = ({ costTrend }) => {
  const { translate } = useTranslation("operations");

  const labels = costTrend.map(p => p.label);

  const data = {
    labels,
    datasets: [
      {
        label: translate("analytics.totalCost"),
        data: costTrend.map(p => p.value),
        backgroundColor: "rgba(16, 185, 129, 0.7)",
        borderRadius: 4,
        maxBarThickness: 24,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#1a1a2e",
        titleFont: { family: "var(--font-sans)", size: 13 },
        bodyFont: { family: "var(--font-mono)", size: 12 },
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          label: (ctx: any) => `$${ctx.parsed.y.toLocaleString(getUiLocale())}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { family: "var(--font-sans)", size: 11 },
          color: dt.textMuted,
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: {
          font: { family: "var(--font-mono)", size: 11 },
          color: dt.textMuted,
          callback: (val: any) => `$${val.toLocaleString(getUiLocale())}`,
        },
      },
    },
  };

  return (
    <Box
      sx={{
        background: dt.cardBg,
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        p: 2.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: dt.font,
          fontSize: "15px",
          fontWeight: 600,
          color: dt.textPrimary,
          mb: 2,
        }}
      >
        {translate("analytics.costTrend")}
      </Typography>
      <Box sx={{ height: 280 }}>
        <Bar data={data} options={options} />
      </Box>
    </Box>
  );
};

export default CostTrendChart;
